import { useState, useEffect } from "react";
import {
  getMessageLogs,
  getMessageStats,
} from "../../../../src/api/communicationTemplates";
import Pagination from "../../../components/AdminPortal/common/Pagination";

export function MessageLogsTab() {
  const [messageLogs, setMessageLogs] = useState([]);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalMessages: 0,
    totalRecipients: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    fetchMessageLogs();
    fetchStats();
  }, [currentPage]);

  const fetchMessageLogs = async () => {
    setLoading(true);
    try {
      const response = await getMessageLogs(currentPage, ITEMS_PER_PAGE);
      setMessageLogs(response.data || []);
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching message logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getMessageStats();

      if (response.success && response.data) {
        setStats({
          totalCustomers: response.data.totalCustomers || 0,
          totalMessages: response.data.totalMessages || 0,
          totalRecipients: response.data.totalRecipients || 0,
          recentMessages: response.data.recentMessages || 0,
          messagesByType: response.data.messagesByType || {},
        });
      } else {
        console.error("Invalid response format:", response);
        setStats({
          totalCustomers: 0,
          totalMessages: 0,
          totalRecipients: 0,
          recentMessages: 0,
          messagesByType: {},
        });
      }
    } catch (error) {
      console.error("Error fetching message stats:", error);
      setStats({
        totalCustomers: 0,
        totalMessages: 0,
        totalRecipients: 0,
        recentMessages: 0,
        messagesByType: {},
      });
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatus = (log) => {
    if (log.scheduledFor && new Date(log.scheduledFor) > new Date()) {
      return "Scheduled";
    }
    return "Sent";
  };

  const getRecipientName = (log) => {
    if (log.receiver && typeof log.receiver === "object") {
      return log.receiver.name || log.receiver.email || log.receiver._id;
    }
    return log.receiver || "-";
  };

  const getMessageType = (log) => {
    if (Array.isArray(log.mode)) {
      return log.mode.join(", ");
    }
    return log.mode || "in-app";
  };

  const getAttachmentList = (log) => {
    if (!log.attachment) return [];
    if (Array.isArray(log.attachment)) return log.attachment;
    return [log.attachment];
  };

  const handleView = (log) => {
    setSelectedMessage(log);
    setViewModalOpen(true);
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#131313] rounded-lg p-4 border border-gray-800 flex justify-between items-center">
          <div>
            <h3 className="text-sm text-gray-400">TOTAL CUSTOMERS</h3>
            <p className="text-3xl font-bold mt-2">
              {stats.totalCustomers || 0}
            </p>
            <p className="text-gray-400 text-sm mt-1">Registered customers</p>
          </div>
          <div className="bg-yellow-400 p-3 rounded-full">
            <div className="text-black">👥</div>
          </div>
        </div>

        <div className="bg-[#131313] rounded-lg p-4 border border-gray-800 flex justify-between items-center">
          <div>
            <h3 className="text-sm text-gray-400">TOTAL MESSAGES SENT</h3>
            <p className="text-3xl font-bold mt-2">
              {stats.totalMessages || 0}
            </p>
            <p className="text-gray-400 text-sm mt-1">Successfully delivered</p>
          </div>
          <div className="bg-yellow-400 p-3 rounded-full">
            <div className="text-black">💬</div>
          </div>
        </div>

        <div className="bg-[#131313] rounded-lg p-4 border border-gray-800 flex justify-between items-center">
          <div>
            <h3 className="text-sm text-gray-400">TOTAL RECIPIENTS</h3>
            <p className="text-3xl font-bold mt-2">
              {stats.totalRecipients || 0}
            </p>
            <p className="text-gray-400 text-sm mt-1">Message recipients</p>
          </div>
          <div className="bg-yellow-400 p-3 rounded-full">
            <div className="text-black">✓</div>
          </div>
        </div>
      </div>

      <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
        <table className="min-w-full text-sm border-separate border-spacing-0">
          <thead className="bg-[#131313] text-white">
            <tr>
              <th className="p-4 text-center border-x border-[#333333] rounded-tl-lg w-[29.4%]">RECIPIENT</th>
              <th className="p-4 text-center border-x border-[#333333] w-[16.6%]">MESSAGE TYPE</th>
              <th className="p-4 text-center border-x border-[#333333] w-[17%]">STATUS</th>
              <th className="p-4 text-center border-x border-[#333333] w-[20%]">DATE & TIME</th>
              <th className="p-4 text-center border-x border-[#333333] rounded-tr-lg w-[17%]">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="text-white bg-[#131313]">
            {loading ? (
              <tr>
                <td colSpan="5" className="p-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : messageLogs.length === 0 ? (
              <tr>
                <td colSpan="5" className="p-6 text-center text-gray-400">
                  No message logs found.
                </td>
              </tr>
            ) : (
              messageLogs.map((log, index) => {
                const isLast = index === messageLogs.length - 1;
                return (
                  <tr
                    key={log._id}
                    className={`border-b border-[#333333] ${
                      isLast ? "last:rounded-b-lg" : ""
                    }`}
                  >
                    <td className="p-4 text-center border-x border-[#333333]">{getRecipientName(log)}</td>
                    <td className="p-4 text-center border-x border-[#333333]">{getMessageType(log)}</td>
                    <td className="p-4 text-center border-x border-[#333333]">
                      <span
                        className={`px-3 py-1 rounded-full text-sm ${
                          getStatus(log) === "Scheduled"
                            ? "bg-yellow-900 text-[#FFCA20]"
                            : getStatus(log) === "Sent"
                            ? "bg-green-900 text-green-400"
                            : "bg-red-900 text-red-400"
                        }`}
                      >
                        {getStatus(log)}
                      </span>
                    </td>
                    <td className="p-4 text-center border-x border-[#333333]">
                      {getStatus(log) === "Scheduled" && log.scheduledFor
                        ? formatDateTime(log.scheduledFor)
                        : formatDateTime(log.sentAt || log.createdAt)}
                    </td>
                    <td className="p-4 text-center border-x border-[#333333]">
                      <button
                        className="text-[#FFCA20] flex items-center justify-center"
                        onClick={() => handleView(log)}
                      >
                        <span className="mr-1">👁</span> View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-4 pt-4 border-t border-[#333333]">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* View Message Modal */}
      {viewModalOpen && selectedMessage && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
          <div className="bg-[#131313] text-white p-6 rounded-lg max-w-2xl w-full border border-gray-700 relative overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-3 right-4 text-gray-400 hover:text-white text-lg"
              onClick={() => setViewModalOpen(false)}
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-4 text-[#FFCA20]">
              Message Details
            </h2>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Recipient</p>
              <p className="text-lg">{getRecipientName(selectedMessage)}</p>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Message Type</p>
              <p className="text-lg">{getMessageType(selectedMessage)}</p>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Status</p>
              <span
                className={`px-3 py-1 rounded-full text-sm ${
                  getStatus(selectedMessage) === "Scheduled"
                    ? "bg-yellow-900 text-[#FFCA20]"
                    : getStatus(selectedMessage) === "Sent"
                    ? "bg-green-900 text-green-400"
                    : "bg-red-900 text-red-400"
                }`}
              >
                {getStatus(selectedMessage)}
              </span>
            </div>
            <div className="mb-3">
              <p className="text-gray-400 text-sm">Message Content</p>
              <pre className="whitespace-pre-wrap bg-black p-3 rounded border border-gray-700 text-sm text-gray-200">
                {selectedMessage.message || "No message content available"}
              </pre>
            </div>
            {getAttachmentList(selectedMessage).length > 0 && (
              <div className="mb-3">
                <p className="text-gray-400 text-sm">Attachments</p>
                <div className="mt-2 space-y-2">
                  {getAttachmentList(selectedMessage).map(
                    (attachment, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-black p-2 rounded border border-gray-700"
                      >
                        <div className="bg-[#FFCA20] text-black p-1 rounded mr-2">
                          {typeof attachment === "string"
                            ? "📎"
                            : attachment.fileType?.includes("image")
                            ? "🖼️"
                            : attachment.fileType?.includes("pdf")
                            ? "📄"
                            : attachment.fileType?.includes("doc")
                            ? "📝"
                            : "📎"}
                        </div>
                        <div className="flex-1 truncate">
                          <p className="text-sm text-white truncate">
                            {attachment.fileName ||
                              (typeof attachment === "string"
                                ? attachment
                                : "Attachment")}
                          </p>
                          <p className="text-xs text-gray-400">
                            {attachment.fileSize
                              ? `${Math.round(attachment.fileSize / 1024)} KB`
                              : ""}
                          </p>
                        </div>
                        {attachment.fileUrl && (
                          <a
                            href={attachment.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#FFCA20] text-sm hover:underline ml-2"
                          >
                            View
                          </a>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
